import type {
  Filter,
  FilterOperator,
  FilterValueCount,
  TreeCategory,
  TreeViewType,
} from '../services/gql/types';
import { ColumnFilterOption } from '../components/Table/types';
import { FilterItem } from '../hooks/useUrlFilters';
import type { TreeType, TreeViewBy } from '../types';

const GQL_FILTER_OPERATORS = new Set<FilterOperator>([
  'contains',
  'equals',
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
  'notContains',
  'notEquals',
]);

const isGqlFilterOperator = (op: string): op is FilterOperator =>
  GQL_FILTER_OPERATORS.has(op as FilterOperator);

export const toGqlFilters = (items: FilterItem[]): Filter[] =>
  items
    .filter((item) => isGqlFilterOperator(item.operator))
    .map(({ property, operator, value }) => ({
      property,
      operator: operator as FilterOperator,
      value,
    }));

const TREE_TYPE_TO_GQL: Record<TreeType, TreeCategory> = {
  data_assets: 'DATA_ASSETS',
  bi_assets: 'BI_ASSETS',
};

const TREE_VIEW_TO_GQL: Record<TreeViewBy, TreeViewType> = {
  dbt: 'DBT',
  dwh: 'DWH',
  tags: 'TAGS',
  owners: 'OWNERS',
  paths: 'PATHS',
};

export const toGqlTreeCategory = (treeType: TreeType): TreeCategory =>
  TREE_TYPE_TO_GQL[treeType];

export const toGqlTreeViewType = (viewBy: TreeViewBy): TreeViewType =>
  TREE_VIEW_TO_GQL[viewBy];

type GraphqlFilterOption<T> = {
  field: T;
  options: FilterValueCount[];
};

const parseFilterOption = (option: FilterValueCount): ColumnFilterOption => {
  let value = option.value;
  try {
    const user = JSON.parse(value);
    if (user?.name) {
      value = user.name;
    } else if (user?.email) {
      value = user.email;
    }
  } catch (error) {
    value = option.value;
  }
  return {
    label: value,
    value,
    count: option.count,
  };
};

export const parseGraphqlFilterOptions = <T extends string>(
  filterOptions: GraphqlFilterOption<T>[],
  fieldToColumnMap: Partial<Record<T, string>>,
) => {
  if (!filterOptions) return {};
  return filterOptions.reduce((acc, fieldOptions) => {
    const column = fieldToColumnMap[fieldOptions.field];
    if (column) {
      acc[column] = fieldOptions.options.map(parseFilterOption);
    }
    return acc;
  }, {} as Record<string, ColumnFilterOption[]>);
};

export const mapUrlFiltersToGraphqlFilters = <T extends string>(
  filters: FilterItem[] | undefined,
  columnToFieldMap: Record<string, T>,
): Filter[] =>
  (filters || [])
    .map((filter): Filter | undefined => {
      const property = columnToFieldMap[filter.property];
      if (!property || !isGqlFilterOperator(filter.operator)) return undefined;
      return {
        property,
        operator: filter.operator,
        value: filter.value,
      };
    })
    .filter((filter): filter is Filter => Boolean(filter));

export const getFilterOptionsOverride = (
  columnId: string,
  filterOptionsOverrides?: Record<string, ColumnFilterOption[]> | null,
  defaultFilterOptions?: ColumnFilterOption[],
) => {
  // returns the default options ONLY if no overrides are provided
  if (!filterOptionsOverrides) return defaultFilterOptions;
  return filterOptionsOverrides[columnId] || defaultFilterOptions;
};
